import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';

// assets

// services

// props
import { IOrderResourceProps } from '../../../../../props/orders/order';
import { IOrderSummaryResourceShortProps } from '../../../../../props/orders/summary';

// components
import { ActionButton, Link, Stack } from '@fluentui/react';
import Text from '../../../../typography/text';
import GeneralService from '../../../../../services/general';
import { observer } from 'mobx-react';
import { useStore } from '../../../../../stores/root';
import { IOrderSalesResourceShortProps } from '../../../../../props/orders/sales';
import OrderService from '../../../../../services/orders/order';
import OrderSalesService from '../../../../../services/orders/sales';
import ErrorService from '../../../../../services/general/error';
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import LoadingComponent from '../../../../feedbacks/loading';
import Label from '../../../../typography/label';
import PermissionsService from '../../../../../services/permissions';

interface OrderDetailsCommissionsProps {
    orderId: string;
}

const OrderDetailsCommissions: React.FC<OrderDetailsCommissionsProps> = observer((props: OrderDetailsCommissionsProps) => {
    const { banner, user } = useStore();

    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [sales, setSales] = React.useState<IOrderSalesResourceShortProps[]>([]);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            setLoaded(false);

            const qs: string[] = [];
            qs.push(`top=all`);
            qs.push(`order_id=${props.orderId}`);

            const _sales = await OrderSalesService.retrieve(qs.join("&"));
            setSales(_sales);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_order_sales',
                text: 'Failed to retrieve order sales. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            })
        }
    }

    const renderSales = (_sales: IOrderSalesResourceShortProps, idx?: number) => {
        const canReadCommission = PermissionsService.hasPermission(['orders.commissions.read.all'], user.permissions) ||
            (PermissionsService.hasPermission(['orders.commissions.read.me'], user.permissions) && _sales.sales.id === user.data?.id);
        let isPaid = false;
        if (_sales.commission && _sales.commission.payslip && Number(_sales.commission.payslip.unpaidAmount || "0") <= 0) {
            isPaid = true;
        }

        const isManual = _sales.commission && Math.abs(Number(_sales.commission.amount) - Number(_sales.calculatedAmount || 0)) > 0.01;

        return <>
            {(idx || 0) > 0 ? <Stack className={'divider'} /> : null}
            <Stack tokens={{ childrenGap: 10 }} horizontal horizontalAlign={'space-between'}>
                <Stack>
                    <NavLink to={"/users/administrators/" + _sales.sales.id}><Label size={'small'}>{_sales.sales.name}</Label></NavLink>
                    {canReadCommission ? (
                        _sales.commission ? (
                            <Stack tokens={{ childrenGap: 2 }}>
                                {_sales.actualDeparture ? (
                                    <Text size={'xsmall'} style={{ fontStyle: 'italic', color: '#666' }}>
                                        Departure: {new Date(_sales.actualDeparture).toLocaleDateString('id-ID')}
                                    </Text>
                                ) : null}
                                <Text size={'xsmall'} style={{ fontStyle: 'italic', color: '#555' }}>
                                    {_sales.commission.description || "Manual Commission"}
                                </Text>
                            </Stack>
                        ) : (
                            <>
                                {Number(_sales.feeAmount) > 0 ? <Text size={'xsmall'}>(Flat Rp. {GeneralService.getNumberWithSeparator(Number(_sales.feeAmount))}, Order Profit: Rp. {GeneralService.getNumberWithSeparator(_sales.nettProfit || 0)})</Text> : null}
                                {Number(_sales.feePercentage) > 0 ? <Text size={'xsmall'}>({GeneralService.getNumberWithSeparator(Number(_sales.feePercentage))}% of Rp. {GeneralService.getNumberWithSeparator(_sales.nettProfit || 0)} order profit)</Text> : null}
                                {Number(_sales.feePercentage) < 1 && Number(_sales.feeAmount) < 1 ? <>
                                    {_sales.contract ? (
                                        <Stack tokens={{ childrenGap: 2 }}>
                                            {_sales.actualDeparture ? (
                                                <Text size={'xsmall'} style={{ fontStyle: 'italic', color: '#666' }}>
                                                    Departure: {new Date(_sales.actualDeparture).toLocaleDateString('id-ID')}
                                                </Text>
                                            ) : null}
                                            <NavLink to={"/employees/contracts/" + _sales.contract.id}>
                                                <Text size={'xsmall'} style={{ fontStyle: 'italic', color: '#0078d4' }}>
                                                    Contract ({new Date(_sales.contract.startDate).toLocaleDateString('id-ID')} - {_sales.contract.endDate ? new Date(_sales.contract.endDate).toLocaleDateString('id-ID') : 'Freelance'})
                                                </Text>
                                            </NavLink>
                                            {_sales.calculatedPercentage !== undefined ? (
                                                <Text size={'xsmall'}>
                                                    ({_sales.calculatedPercentage}%
                                                    {_sales.shareProfit !== undefined && _sales.nettProfit !== undefined && _sales.shareProfit !== _sales.nettProfit ? (
                                                        ` of Rp. ${GeneralService.getNumberWithSeparator(_sales.shareProfit)} shared profit / total Rp. ${GeneralService.getNumberWithSeparator(_sales.nettProfit)} order profit)`
                                                    ) : (
                                                        ` of Rp. ${GeneralService.getNumberWithSeparator(_sales.nettProfit || 0)} order profit)`
                                                    )}
                                                </Text>
                                            ) : null}
                                        </Stack>
                                    ) : (
                                        <>
                                            {!_sales.actualDeparture ? (
                                                <Text size={'xsmall'} style={{ fontStyle: 'italic', color: '#e81123' }}>(Belum Berangkat)</Text>
                                            ) : (
                                                <Text size={'xsmall'} style={{ fontStyle: 'italic' }}>(as per contract)</Text>
                                            )}
                                        </>
                                    )}
                                </> : null}
                            </>
                        )
                    ) : null}
                </Stack>
                <Stack tokens={{ childrenGap: 2 }} horizontalAlign={'end'}>
                    {canReadCommission && _sales.commission ? <>
                        <Label size={'small'}>Rp. {GeneralService.getNumberWithSeparator(Number(_sales.commission.amount))}</Label>
                        <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign={'center'}>
                            {_sales.commission.payslip ? (
                                <Link target={"_blank"} href={'/hr/payslips/' + _sales.commission.payslip.id}><Text size={'small'}>#{GeneralService.parseId(_sales.commission.payslip.id || "")}</Text></Link>
                            ) : null}
                            <Text size={'small'} className={isPaid ? 'color-green' : 'color-orange'}>
                                {isPaid ? '(Paid)' : '(Unpaid)'}
                            </Text>
                            <Text size={'xsmall'} style={{ fontStyle: 'italic', color: '#666', marginLeft: 4 }}>
                                ({isManual ? 'Manual' : 'Percentage'})
                            </Text>
                        </Stack>
                    </> : null}
                    {canReadCommission && !_sales.commission ? <>
                        {!_sales.actualDeparture ? (
                            <Text size={'small'} style={{ fontStyle: 'italic', color: '#e81123', marginTop: 2 }}>-</Text>
                        ) : (
                            <Text size={'small'} style={{ fontStyle: 'italic', marginTop: 2 }}>(TBA)</Text>
                        )}
                    </> : null}
                </Stack>
            </Stack>
        </>
    }

    return <Stack tokens={{ childrenGap: 10 }}>
        {!loaded ? <LoadingComponent label={'Retrieving order sales ...'} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
        {loaded && sales.length < 1 ? <Text size={'small'} style={{ fontStyle: 'italic' }}>No sales/commissions</Text> : null}
        {loaded && sales.length > 0 ? <Stack tokens={{ childrenGap: 10 }}>
            {(sales || []).map(renderSales)}
        </Stack> : null}
    </Stack>
});

export default OrderDetailsCommissions;
